import { reaction } from "mobx";
import { observer } from "mobx-react-lite";
import React from "react";
import { Linking } from "react-native";
import { PlaniraLogType } from "../../../shared/types";
import { ModuleType, PlanQuestionType } from "../../../templates/types";
import { trackEvent } from "../../core/trackEvent";
import type CalendarModuleModel from "../../modules/Calendar/CalendarModuleModel";
import type LocationModel from "../../modules/Location/LocationModel";
import { log } from "../../ui/log";
import { findCheckinAndCheckout } from "../utils";
import type { SearchHotelsParams } from "./apis";
import { HotelsApiManager } from "./HotelsApiManager";
import { HotelsTipsBar } from "./HotelsTipsBar";

export const LocationItemHotelsTipsUI = observer(
  ({ locationItem }: { locationItem: LocationModel }) => {
    const [manager] = React.useState(
      () =>
        new HotelsApiManager((newCache) => {
          locationItem.updateCache(newCache);
        }, locationItem.data.cache.affiliateHotelsDump),
    );

    const plan = locationItem.plan;

    const modules = plan.modules;

    const datesModule =
      (modules.find(
        (m) => m.data.questionType === PlanQuestionType.Dates && m.isDone,
      ) as CalendarModuleModel | undefined) ??
      (modules.find((m) => m.data.type === ModuleType.Calendar && m.isDone) as
        | CalendarModuleModel
        | undefined);

    React.useEffect(() => {
      // Combined reaction for selected location and selected dates
      const combinedReaction = reaction(
        () => {
          const selectedLocation = locationItem.data.mapData;
          const selectedDates = datesModule?.data.selectedDates;
          return { selectedLocation, selectedDates };
        },
        ({ selectedLocation, selectedDates }) => {
          log.dev("Got tips data", { selectedLocation, selectedDates });

          if (selectedLocation) {
            const params: Partial<SearchHotelsParams> = {
              latitude: selectedLocation.lat,
              longitude: selectedLocation.lng,
            };

            if (selectedDates?.length) {
              const dates = findCheckinAndCheckout(selectedDates);

              if (dates.checkin_date && dates.checkout_date) {
                params.checkin_date = dates.checkin_date;
                params.checkout_date = dates.checkout_date;
              }
            }

            manager.updateParams(params);
          }
        },
        { fireImmediately: true },
      );

      // Cleanup reaction when the component unmounts
      return () => {
        combinedReaction();
      };
    }, [manager, locationItem?.data, datesModule?.data]);

    if (!manager.hotels.length) {
      return null;
    }

    return (
      <HotelsTipsBar
        daysCount={manager.daysCount}
        onSelect={(tip) => {
          if (tip.url) {
            const affiliateUrl = manager.getAffiliateUrl(tip.url);

            trackEvent("affiliate_hotels_click");

            void plan.addPlaniraLogItem(
              PlaniraLogType.TappedOnTip,
              affiliateUrl,
            );

            Linking.openURL(affiliateUrl); // Open the external link
          }
        }}
        tips={manager.hotels}
      />
    );
  },
);
